import { useIsFocused, useNavigation } from 'expo-router/react-navigation';
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';

type Candidate = { isFocused: () => boolean; hasContent: boolean };

/**
 * Who draws in the slot. Headers register in mount order and the owner is the first one that is
 * focused and has something to show, so the choice depends only on that order and on focus, never
 * on which header happened to ask first.
 */
function createHeaderSlotStore() {
  const candidates = new Map<symbol, Candidate>();

  const listeners = new Set<() => void>();

  let target: HTMLElement | null = null;

  let owner: symbol | null = null;

  const notify = () => listeners.forEach((listener) => listener());

  const recompute = () => {
    let next: symbol | null = null;

    for (const [id, candidate] of candidates) {
      if (candidate.hasContent && candidate.isFocused()) {
        next = id;

        break;
      }
    }

    if (next !== owner) {
      owner = next;

      notify();
    }
  };

  return {
    subscribe(listener: () => void) {
      listeners.add(listener);

      return () => listeners.delete(listener);
    },
    targetFor: (id: symbol) => (owner === id ? target : null),
    setTarget(next: HTMLElement | null) {
      target = next;

      notify();
    },
    register(id: symbol, candidate: Candidate) {
      candidates.set(id, candidate);

      recompute();
    },
    update(id: symbol, hasContent: boolean) {
      const candidate = candidates.get(id);

      if (candidate) {
        candidate.hasContent = hasContent;
      }

      recompute();
    },
    unregister(id: symbol) {
      candidates.delete(id);

      recompute();
    },
    recompute,
  };
}

type HeaderSlotStore = ReturnType<typeof createHeaderSlotStore>;

const HeaderSlotContext = createContext<HeaderSlotStore | null>(null);

/**
 * Holds the one place under the nav bar where the current page's web `Header` is drawn.
 *
 * A page's header used to render wherever the screen put it, so a screen that centered and
 * capped its column moved Back with the column, and Back landed somewhere different on every
 * page. Drawing every page's header in the layout makes its position independent of the screen:
 * Back sits under the logo and the header's right items under the account menu, whatever the
 * screen's layout. Mount one provider per layout that draws a nav bar, with `HeaderSlot` placed
 * where the header belongs.
 *
 * The provider's value never changes, so a handoff re-renders only the two headers involved.
 */
export function HeaderSlotProvider({ children }: { children: ReactNode }) {
  const [store] = useState(createHeaderSlotStore);

  return <HeaderSlotContext.Provider value={store}>{children}</HeaderSlotContext.Provider>;
}

/** Where the focused page's header is drawn: directly under the nav bar and the global banners. */
export function HeaderSlot() {
  const store = useContext(HeaderSlotContext);

  const node = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!store) {
      return undefined;
    }

    store.setTarget(node.current);

    return () => store.setTarget(null);
  }, [store]);

  return <div ref={node} style={{ width: '100%' }} />;
}

const noSubscribe = () => () => undefined;

const noTarget = () => null;

/**
 * Draws `children` in the layout's header slot when this is the focused page's header, or in
 * place otherwise.
 *
 * The slot goes to the first mounted header that is focused and has content. A page draws its
 * header before any overlay that carries its own, such as a full-screen wizard, so the page keeps
 * the slot and the overlay's header stays in place. A page header whose content arrives late still
 * wins once it has some, and when the owner leaves, the next header in line takes over in the same
 * commit, so the slot is never left empty while a focused header could fill it. A screen kept
 * mounted beneath the current one is not focused, so its header gives way to the page on top.
 *
 * `children` always render into one detached element, which is moved between the slot and this
 * spot, so a handoff never remounts the header or loses an open menu. Outside a provider, as in
 * tests, every header renders in place.
 */
export function InHeaderSlot({
  children,
  hasContent = true,
}: {
  children?: ReactNode;
  hasContent?: boolean;
}) {
  const store = useContext(HeaderSlotContext);

  const navigation = useNavigation();

  const isFocused = useIsFocused();

  const [id] = useState(() => Symbol('header'));

  const navigationRef = useRef(navigation);

  navigationRef.current = navigation;

  const [container] = useState(() => {
    if (!store || typeof document === 'undefined') {
      return null;
    }

    const element = document.createElement('div');

    element.style.display = 'contents';

    return element;
  });

  const placeholder = useRef<HTMLDivElement>(null);

  const target = useSyncExternalStore(
    store ? store.subscribe : noSubscribe,
    store ? () => store.targetFor(id) : noTarget
  );

  useLayoutEffect(() => {
    if (!store) {
      return undefined;
    }

    store.register(id, { isFocused: () => navigationRef.current.isFocused(), hasContent });

    return () => store.unregister(id);
    // Registering once keeps this header's place in mount order; content and focus update below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, id]);

  useLayoutEffect(() => {
    store?.update(id, hasContent);
  }, [store, id, hasContent, isFocused]);

  useLayoutEffect(() => {
    const parent = target ?? placeholder.current;

    if (container && parent && container.parentNode !== parent) {
      parent.appendChild(container);
    }
  }, [container, target]);

  useLayoutEffect(() => () => container?.remove(), [container]);

  if (!container) {
    return <>{children}</>;
  }

  return (
    <>
      <div ref={placeholder} style={{ display: 'contents' }} />
      {createPortal(children, container)}
    </>
  );
}
